import { useEffect, useRef, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Slider } from "@/components/ui/slider";

interface Props {
  /** Current value */
  value: number;
  /** Formatted display string (e.g. "$2,500/mo", "60", "$750K") */
  display: string;
  /** Commit a new value */
  onCommit: (v: number) => void;
  /** Slider range */
  min: number;
  max: number;
  step?: number;
  /** Hard ceiling for typed value (default = max) */
  hardMax?: number;
  /** Prefix/suffix in the editor input (e.g. "$", "yrs") */
  prefix?: string;
  suffix?: string;
  /** Label above the input in the popover */
  label: string;
  /** Color of the underlined chip */
  tone?: "primary" | "foreground";
  ariaLabel?: string;
}

/**
 * Inline-editable number chip: looks like an underlined word in a sentence;
 * click opens a small popover with a slider + numeric input. Commits on close.
 */
const EditableNumber = ({
  value,
  display,
  onCommit,
  min,
  max,
  step = 1,
  hardMax,
  prefix,
  suffix,
  label,
  tone = "primary",
  ariaLabel,
}: Props) => {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState(String(value));
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) setText(String(Math.round(value)));
  }, [value, open]);

  useEffect(() => {
    if (open) {
      // small delay so popover is mounted before selecting
      requestAnimationFrame(() => inputRef.current?.select());
    }
  }, [open]);

  const ceil = hardMax ?? max;
  const clamp = (n: number) => Math.min(ceil, Math.max(min, n));
  const sliderValue = clamp(value);

  const commit = (raw: string | number) => {
    const n = typeof raw === "number" ? raw : Number(String(raw).replace(/,/g, "").trim());
    if (!Number.isFinite(n)) return;
    onCommit(clamp(Math.round(n)));
  };

  const toneClass =
    tone === "primary"
      ? "text-primary decoration-primary/40 hover:decoration-primary"
      : "text-foreground decoration-foreground/30 hover:decoration-foreground";

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={ariaLabel ?? label}
          className={`inline-flex items-baseline underline decoration-2 underline-offset-4 transition-colors rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 ${toneClass}`}
        >
          {display}
        </button>
      </PopoverTrigger>
      <PopoverContent
        sideOffset={8}
        align="start"
        className="w-72 p-4 rounded-2xl"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-2">
          {label}
        </div>
        <div className="flex items-center gap-1 rounded-lg border border-border bg-background px-2 py-1.5 mb-3 focus-within:border-primary focus-within:ring-1 focus-within:ring-primary/30 transition-colors">
          {prefix && <span className="text-sm text-muted-foreground select-none">{prefix}</span>}
          <input
            ref={inputRef}
            type="text"
            inputMode="numeric"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                commit(e.currentTarget.value);
                setOpen(false);
              }
              if (e.key === "Escape") setOpen(false);
            }}
            onBlur={(e) => commit(e.currentTarget.value)}
            className="flex-1 bg-transparent text-end text-base font-bold tabular-nums text-foreground outline-none"
          />
          {suffix && <span className="text-sm text-muted-foreground select-none">{suffix}</span>}
        </div>
        <Slider
          value={[sliderValue]}
          min={min}
          max={max}
          step={step}
          onValueChange={(v) => {
            setText(String(v[0]));
            commit(v[0]);
          }}
          aria-label={label}
        />
        <div className="mt-2 flex justify-between text-[10px] text-muted-foreground tabular-nums">
          <span>{min}</span>
          <span>{max}</span>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default EditableNumber;
